import { useId, useState } from 'react';

export interface SeriesPoint {
  at: string;
  /** Null where the platform has no reading. Drawn as a break, never interpolated. */
  value: number | null;
}

export interface Series {
  label: string;
  color: string;
  points: SeriesPoint[];
}

/**
 * A time-series line chart in plain SVG.
 *
 * ## No charting library
 *
 * Gridlines, an axis and a hover readout are perhaps two hundred lines of SVG. A library would be
 * ~100 KB added to a bundle Vite already warns about, and would then have to be fought to match
 * the rest of the interface. The trade goes the other way here.
 *
 * ## Gaps break the line
 *
 * Same rule as {@see Sparkline}: a null is a period the platform did not observe. Joining across it
 * would draw a confident line through nothing, and zeroing it would claim the machine was idle.
 * Each contiguous run of readings is its own polyline.
 *
 * ## The y-axis starts at zero
 *
 * Deliberately, even though auto-scaling would make small variations look dramatic. That is
 * precisely why not: a CPU line wobbling between 3% and 4% should look flat, because it is. An
 * axis that starts at 2.9% turns noise into a mountain range, which is the most common way a chart
 * lies without anybody intending it to.
 */
export function TimeSeriesChart({
  series,
  height = 260,
  /** Fixed ceiling for percentages. Omit to scale to the data. */
  max,
  formatValue = (v) => String(Math.round(v)),
  emptyMessage = 'No data for this period yet.',
}: {
  series: Series[];
  height?: number;
  max?: number;
  formatValue?: (value: number) => string;
  emptyMessage?: string;
}) {
  const clipId = useId();
  const [hover, setHover] = useState<number | null>(null);

  const length = Math.max(0, ...series.map((s) => s.points.length));
  const known = series.flatMap((s) =>
    s.points.map((p) => p.value).filter((v): v is number => v !== null),
  );

  if (length === 0 || known.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground"
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  const width = 800;
  const padLeft = 44;
  const padBottom = 22;
  const padTop = 8;
  const plotWidth = width - padLeft;
  const plotHeight = height - padBottom - padTop;

  const ceiling = max ?? Math.max(1, ...known) * 1.15;
  const step = length > 1 ? plotWidth / (length - 1) : 0;

  const x = (index: number) => padLeft + index * step;
  const y = (value: number) =>
    padTop + plotHeight - (Math.max(0, Math.min(ceiling, value)) / ceiling) * plotHeight;

  const gridValues = [0, 0.25, 0.5, 0.75, 1].map((f) => ceiling * f);

  // Four labels regardless of how many points there are, so a 30-day window is not an unreadable
  // smear of timestamps.
  const labelIndexes =
    length <= 1 ? [0] : [0, Math.floor(length / 3), Math.floor((length * 2) / 3), length - 1];

  const timestamps = series[0]?.points.map((p) => p.at) ?? [];

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        style={{ height }}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientX - box.left) / box.width;
          const index = Math.round(((ratio * width - padLeft) / plotWidth) * (length - 1));
          setHover(index >= 0 && index < length ? index : null);
        }}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={padLeft} y={0} width={plotWidth} height={height} />
          </clipPath>
        </defs>

        {gridValues.map((value) => (
          <g key={value}>
            <line
              x1={padLeft}
              x2={width}
              y1={y(value)}
              y2={y(value)}
              stroke="currentColor"
              strokeWidth={1}
              strokeDasharray="2 4"
              className="text-muted-foreground/25"
            />
            <text
              x={padLeft - 8}
              y={y(value) + 3}
              textAnchor="end"
              className="fill-muted-foreground text-[9px]"
            >
              {formatValue(value)}
            </text>
          </g>
        ))}

        {series.map((line) => {
          // Contiguous runs only — a null ends the current run and starts a new one.
          const runs: string[] = [];
          let current: string[] = [];

          line.points.forEach((point, index) => {
            if (point.value === null) {
              if (current.length > 1) runs.push(current.join(' '));
              current = [];

              return;
            }

            current.push(`${x(index)},${y(point.value)}`);
          });

          if (current.length > 1) runs.push(current.join(' '));

          return (
            <g key={line.label} clipPath={`url(#${clipId})`}>
              {runs.map((run, index) => (
                <polyline
                  key={index}
                  points={run}
                  fill="none"
                  stroke={line.color}
                  strokeWidth={1.75}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </g>
          );
        })}

        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={padTop}
            y2={padTop + plotHeight}
            stroke="currentColor"
            strokeWidth={1}
            className="text-muted-foreground/50"
          />
        )}

        {labelIndexes.map((index) => (
          <text
            key={index}
            x={x(index)}
            y={height - 6}
            textAnchor={index === 0 ? 'start' : index === length - 1 ? 'end' : 'middle'}
            className="fill-muted-foreground text-[9px]"
          >
            {timestamps[index] === undefined
              ? ''
              : new Date(timestamps[index]).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
          </text>
        ))}
      </svg>

      {hover !== null && timestamps[hover] !== undefined && (
        <div className="pointer-events-none absolute right-2 top-2 rounded-md border bg-background/95 px-2 py-1 text-xs shadow-sm">
          <p className="text-muted-foreground">
            {new Date(timestamps[hover]).toLocaleString([], {
              hour: '2-digit',
              minute: '2-digit',
              day: 'numeric',
              month: 'short',
            })}
          </p>
          {series.map((line) => {
            const point = line.points[hover];

            return (
              <p key={line.label} className="flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="size-2 rounded-full"
                  style={{ background: line.color }}
                />
                {line.label}:{' '}
                <span className="font-medium">
                  {/* "no reading" rather than a dash, so the gap is explained rather than blank. */}
                  {point === undefined || point.value === null
                    ? 'no reading'
                    : formatValue(point.value)}
                </span>
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
}
